import { useQuery, useMutation, useQueryClient, useIsRestoring } from '@tanstack/react-query';
import { useMemo, useEffect } from 'react';
import {
  getCurrentAuthUser,
  signOutUser,
  getAccessToken,
  getIdToken,
  type AuthUser,
} from '../lib/auth';
import { useNavigate } from '@tanstack/react-router';
import { persister } from '../lib/api/query-client';
import { disconnectSocket } from '../lib/socket/socket';
import { satisfiesPermission } from '../lib/permissions';

type AuthState =
  | { status: 'loading' }
  | { status: 'authenticated'; user: AuthUser }
  | { status: 'unauthenticated' };

function resolveLoginRedirectTarget(rawPath: string): string {
  const maxDepth = 12;
  let candidate = rawPath;

  for (let i = 0; i < maxDepth; i += 1) {
    if (!candidate.startsWith('/') || candidate.startsWith('//')) {
      return '/dashboard';
    }

    if (candidate === '/login') {
      return '/dashboard';
    }

    if (candidate.startsWith('/login?')) {
      const params = new URLSearchParams(candidate.slice('/login?'.length));
      const nested = params.get('redirect');
      if (!nested) {
        return '/dashboard';
      }
      candidate = nested;
      continue;
    }

    return candidate;
  }

  return '/dashboard';
}

export function useAuth() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const isRestoring = useIsRestoring();
  const hasToken = !!getAccessToken() && !!getIdToken();

  const {
    data: user,
    isLoading: queryIsLoading,
    isFetchedAfterMount,
    error,
    refetch: refetchUser,
  } = useQuery<AuthUser | null>({
    queryKey: ['auth', 'user'],
    queryFn: getCurrentAuthUser,
    // Always revalidate auth state on mount; persisted cache can contain stale null.
    staleTime: 0,
    refetchOnMount: 'always',
    gcTime: 1000 * 60 * 60 * 24 * 90,
    retry: false,
    enabled: hasToken && !isRestoring,  // <-- don't even start until cache is restored
  });

  // Derive auth state synchronously — no useEffect, no setState, no flicker.
  const authState = useMemo<AuthState>(() => {
    // Still restoring persisted cache
    if (isRestoring) {
      return { status: 'loading' };
    }
    // Optimistic: a cached user (from the persisted react-query cache) is shown
    // immediately, even while the background revalidation runs. Without this, a
    // component that remounts — e.g. the mobile sidebar sheet reopening — reads
    // `loading` on its fresh observer (refetchOnMount: 'always') and blanks its
    // permission-gated content until the refetch resolves. The cache is kept and
    // updated in place instead, so the nav stays populated.
    if (hasToken && user) {
      return { status: 'authenticated', user };
    }
    // No cached user yet, but a token is present: don't decide unauthenticated
    // until the first post-mount validation completes. This avoids refresh
    // flicker when the persisted cache contains a stale null user.
    if (hasToken && (queryIsLoading || !isFetchedAfterMount)) {
      return { status: 'loading' };
    }
    // Everything else: no token, null user, failed query, etc.
    return { status: 'unauthenticated' };
  }, [isRestoring, hasToken, queryIsLoading, isFetchedAfterMount, user]);

  const wasUnauthenticated = authState.status === 'unauthenticated';
  useEffect(() => {
    if (wasUnauthenticated) {
      disconnectSocket();
      queryClient.setQueryData(['auth', 'user'], null);
      Promise.resolve(persister.removeClient()).catch(() => {});
    }
  }, [wasUnauthenticated, queryClient]);

  const signOutMutation = useMutation({
    mutationFn: (globalSignOut: boolean = false) => signOutUser(globalSignOut),
    onSuccess: async () => {
      const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      const redirectTarget = resolveLoginRedirectTarget(currentPath);

      // Tear down the notifications socket so it stops using the now-cleared token
      // (the server also force-disconnects on a sign-out-everywhere).
      disconnectSocket();
      queryClient.clear();
      await persister.removeClient();
      navigate({ to: '/login', search: { redirect: redirectTarget }, replace: true });
    },
  });

  const hasPermission = (permission: string): boolean => {
    if (authState.status !== 'authenticated') return false;
    return satisfiesPermission(authState.user.permissions, permission);
  };

  const hasRole = (roleCode: string): boolean => {
    if (authState.status !== 'authenticated') return false;
    return authState.user.roles?.some((r) => r.code === roleCode) ?? false;
  };

  return {
    user: authState.status === 'authenticated' ? authState.user : null,
    isLoading: authState.status === 'loading',
    isAuthenticated: authState.status === 'authenticated',
    authState, // expose the discriminated union directly if callers want exhaustive matching
    error,
    signOut: (globalSignOut: boolean = false) => signOutMutation.mutate(globalSignOut),
    isSigningOut: signOutMutation.isPending,
    hasPermission,
    hasRole,
    refetchUser,
  };
}